import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Check, LogOut, Sparkles, Wand2, Puzzle, SlidersHorizontal, ChevronRight, FileText, Shield, Lock, CreditCard, Ticket, MessageCircle, ShieldAlert, Flame, LogIn, Waves, Pencil, Gem, Share2 } from 'lucide-react';
import PageShell from '../components/PageShell';
import StreakCard, { getActiveMilestone } from '../components/StreakCard';
import { useSubscription } from '../contexts/SubscriptionContext';
import { motion, AnimatePresence } from 'framer-motion';
import { authFetch, API } from '../utils/api';


export default function Profile({ user, setUser, onLogout }) {
  const navigate = useNavigate();
  const { isPro } = useSubscription();
  const [form, setForm] = useState({
    name: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [originalName, setOriginalName] = useState('');
  const [aura, setAura] = useState(0);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [engagement, setEngagement] = useState(null);
  const [showStreakCard, setShowStreakCard] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, ticketsRes, engRes] = await Promise.all([
          authFetch(`${API}/api/profile`),
          authFetch(`${API}/api/tickets`),
          authFetch(`${API}/api/profile/engagement`),
        ]);
        if (profileRes.ok) {
          const data = await profileRes.json();
          setForm({ name: data.name || '' });
          setOriginalName(data.name || '');
          setAura(data.aura || 0);
        }
        if (ticketsRes.ok) {
          const tData = await ticketsRes.json();
          const open = (tData.tickets || []).filter(t => t.status === 'open' || t.status === 'in_progress').length;
          setOpenTicketCount(open);
        }
        if (engRes.ok) {
          setEngagement(await engRes.json());
        }
      } catch {} finally { setLoading(false); }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await authFetch(`${API}/api/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
      });
      if (res.ok) {
        const updated = await res.json();
        if (setUser) setUser(updated);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch {} finally { setSaving(false); }
  };

  if (loading) {
    return (
      <PageShell title="Profile">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Profile">
      <div className="p-5 space-y-6" data-testid="profile-page">
        {/* Avatar + Aura */}
        <div className="flex items-center gap-3">
          <div className={`w-14 h-14 rounded-full overflow-hidden ${isPro ? 'ring-2 ring-amber-400/70 shadow-[0_0_10px_rgba(251,191,36,0.35)]' : 'border-2 border-white/10'}`}>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center text-lg font-bold">
                {(user?.name || '?')[0].toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">{user?.email}</p>
            <p className="text-[10px] text-muted-foreground">connected via Google</p>
          </div>
          <div className="flex items-center gap-1.5 bg-secondary/10 border border-secondary/20 rounded-xl px-3 py-2" data-testid="profile-aura-score">
            <Sparkles className="w-3.5 h-3.5 text-secondary" />
            <span className="text-sm font-bold text-secondary">{aura}</span>
            <span className="text-[9px] text-secondary/70">aura</span>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs tracking-widest uppercase text-muted-foreground">NAME</label>
              {!editing && (
                <button
                  data-testid="profile-edit-name-btn"
                  onClick={() => setEditing(true)}
                  className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground/50 hover:text-primary transition-colors" />
                </button>
              )}
            </div>
            {editing ? (
              <input
                data-testid="profile-name-input"
                type="text"
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                autoFocus
                className="w-full bg-white/5 border border-primary/30 rounded-xl px-3 py-3 text-sm outline-none focus:border-primary/50 transition-colors"
              />
            ) : (
              <p className="text-sm px-3 py-3 rounded-xl bg-white/[0.03] border border-white/5 text-foreground" data-testid="profile-name-display">
                {form.name || 'No name set'}
              </p>
            )}
          </div>
        </div>

        {editing && (
          <div className="flex gap-2">
            <button
              data-testid="profile-cancel-edit-btn"
              onClick={() => { setForm({...form, name: originalName}); setEditing(false); }}
              className="flex-1 bg-white/5 border border-white/10 hover:border-white/20 font-medium text-sm py-3 rounded-2xl transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="profile-save-btn"
              onClick={() => { handleSave(); setOriginalName(form.name); setEditing(false); }}
              disabled={saving}
              className="flex-1 bg-primary text-primary-foreground hover:opacity-90 font-bold text-sm py-3 rounded-2xl transition-opacity inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saved ? <><Check className="w-4 h-4" /> Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save</>}
            </button>
          </div>
        )}

        {/* Engagement Stats */}
        {engagement && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/[0.02] border border-white/5 rounded-2xl p-4"
            data-testid="engagement-stats"
          >
            <p className="text-[9px] uppercase tracking-widest text-muted-foreground/40 mb-3">Your Activity</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <Flame className="w-3 h-3 text-orange-400" />
                  <span className="text-lg font-heading font-bold text-orange-400" data-testid="current-streak">{engagement.login_streak}</span>
                </div>
                <p className="text-[8px] text-muted-foreground/40">day streak</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <Flame className="w-3 h-3 text-amber-400/50" />
                  <span className="text-lg font-heading font-bold text-foreground" data-testid="best-streak">{engagement.best_login_streak}</span>
                </div>
                <p className="text-[8px] text-muted-foreground/40">best streak</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-0.5">
                  <LogIn className="w-3 h-3 text-primary/50" />
                  <span className="text-lg font-heading font-bold text-foreground" data-testid="total-logins">{engagement.total_logins}</span>
                </div>
                <p className="text-[8px] text-muted-foreground/40">total logins</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <MessageCircle className="w-2.5 h-2.5 text-primary/50" />
                  <span className="text-xs font-bold" data-testid="total-messages">{engagement.total_messages}</span>
                </div>
                <p className="text-[7px] text-muted-foreground/30 mt-0.5">msgs with Swell</p>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Waves className="w-2.5 h-2.5 text-cyan-400/50" />
                  <span className="text-xs font-bold" data-testid="total-sessions">{engagement.total_sessions}</span>
                </div>
                <p className="text-[7px] text-muted-foreground/30 mt-0.5">surf sessions</p>
              </div>
              <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                <span className="text-xs font-bold" data-testid="days-active">{engagement.days_active}</span>
                <p className="text-[7px] text-muted-foreground/30 mt-0.5">days active</p>
              </div>
            </div>

            {/* Share Streak Button */}
            {engagement.login_streak >= 7 && (
              <button
                onClick={() => setShowStreakCard(true)}
                className="w-full mt-3 border border-orange-500/20 hover:border-orange-500/40 rounded-xl px-3 py-2 flex items-center justify-center gap-2 transition-all group"
                style={{ background: 'rgba(255,107,53,0.04)' }}
                data-testid="share-streak-btn"
              >
                <Share2 className="w-3 h-3 text-orange-400/60 group-hover:text-orange-400 transition-colors" />
                <span className="text-[10px] font-semibold text-orange-400/70 group-hover:text-orange-400 transition-colors">
                  Share your {engagement.login_streak}-day streak
                </span>
              </button>
            )}
          </motion.div>
        )}

        {/* Treasure Chest Link */}
        <button
          data-testid="profile-treasure-chest-link"
          onClick={() => navigate('/treasure-chest')}
          className="w-full bg-gradient-to-r from-amber-500/[0.06] to-violet-500/[0.06] border border-amber-400/15 hover:border-amber-400/30 rounded-2xl px-4 py-4 flex items-center gap-3 transition-colors group"
        >
          <Gem className="w-5 h-5 text-amber-400/80 group-hover:text-amber-400 transition-colors" />
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-amber-200/90">Treasure Chest</p>
            <p className="text-[9px] text-muted-foreground/50">Unlock rewards & milestones</p>
          </div>
          <ChevronRight className="w-4 h-4 text-amber-400/30" />
        </button>

        {/* Settings Links */}
        <div className="space-y-2">
          <button
            data-testid="profile-swell-skills-link"
            onClick={() => navigate('/swell-skills')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <Wand2 className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">Swell Skills</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
          <button
            data-testid="profile-integrations-link"
            onClick={() => navigate('/integrations')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <Puzzle className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">Integrations</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
          <button
            data-testid="profile-preferences-link"
            onClick={() => navigate('/preferences')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <SlidersHorizontal className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">Preferences</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
        </div>

        {/* Manage */}
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 px-1 mb-1">Manage</p>
          <button
            data-testid="profile-billing-link"
            onClick={() => navigate('/billing')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <CreditCard className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">Billing</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
          <button
            data-testid="profile-tickets-link"
            onClick={() => navigate('/tickets')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <Ticket className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">My Tickets</span>
            {openTicketCount > 0 && (
              <span className="text-[9px] bg-primary/15 text-primary font-bold px-1.5 py-0.5 rounded-full">{openTicketCount}</span>
            )}
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
          {user?.is_admin && (
            <button
              data-testid="profile-admin-link"
              onClick={() => navigate('/admin')}
              className="w-full bg-amber-500/[0.06] border border-amber-500/15 hover:border-amber-500/30 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
            >
              <ShieldAlert className="w-4 h-4 text-amber-400/70 group-hover:text-amber-400 transition-colors" />
              <span className="text-sm flex-1 text-left text-amber-200/80">Admin</span>
              <span className="text-[8px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded-full font-bold">ADMIN</span>
              <ChevronRight className="w-4 h-4 text-amber-400/30" />
            </button>
          )}
        </div>

        {/* Legal */}
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground/50 px-1 mb-1">Legal</p>
          <button
            data-testid="profile-terms-link"
            onClick={() => navigate('/terms')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <FileText className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">Terms</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
          <button
            data-testid="profile-privacy-link"
            onClick={() => navigate('/privacy')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <Shield className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">Privacy</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
          <button
            data-testid="profile-security-link"
            onClick={() => navigate('/security')}
            className="w-full bg-white/5 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors group"
          >
            <Lock className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-sm flex-1 text-left">Security</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground/40" />
          </button>
        </div>

        {/* Sign Out */}
        <button
          data-testid="profile-sign-out-btn"
          onClick={onLogout}
          className="w-full bg-white/5 border border-white/10 hover:border-accent/30 hover:bg-accent/5 text-muted-foreground hover:text-accent text-sm py-3 rounded-2xl transition-all inline-flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>

      {/* Streak Card Modal */}
      <AnimatePresence>
        {showStreakCard && (
          <StreakCard
            user={{...user, login_streak: engagement?.login_streak, best_login_streak: engagement?.best_login_streak, total_logins: engagement?.total_logins}}
            onClose={() => setShowStreakCard(false)}
          />
        )}
      </AnimatePresence>
    </PageShell>
  );
}
